import { createFileRoute } from "@tanstack/react-router"
import {
	IconKey,
	IconWebhook,
	IconCreditCard,
	IconUser,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import { API_ENDPOINTS, QUERY_KEYS, CACHE_TIME } from "@/lib/constants"

export const Route = createFileRoute("/settings")({
	component: SettingsPage,
})

function SettingsPage() {
	return (
		<ProtectedRoute>
			<AppShell>
				<SettingsContent />
			</AppShell>
		</ProtectedRoute>
	)
}

function SettingsContent() {
	const { data: settings, isLoading, error } = useMerchantSettings()

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
			</div>
		)
	}

	if (error || !settings) {
		return (
			<div className="flex items-center justify-center h-64">
				<p className="text-destructive">Failed to load settings</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Settings</h1>
				<p className="text-muted-foreground">
					Manage your merchant account settings and preferences
				</p>
			</div>

			{/* Account Information */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconUser className="h-5 w-5" />
						Account Information
					</CardTitle>
					<CardDescription>
						Basic information about your merchant account
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 md:grid-cols-2">
						<div className="space-y-2">
							<Label>Business Name</Label>
							<Input value={settings.name} readOnly />
						</div>
						<div className="space-y-2">
							<Label>Email</Label>
							<Input value={settings.email} readOnly />
						</div>
						<div className="space-y-2">
							<Label>Commission Rate</Label>
							<Input value={`${(settings.commission_rate * 100).toFixed(2)}%`} readOnly />
						</div>
						<div className="space-y-2">
							<Label>Status</Label>
							<div>
								<Badge variant={settings.status === "active" ? "default" : "secondary"}>
									{settings.status}
								</Badge>
							</div>
						</div>
					</div>
					<Separator />
					<div className="space-y-2">
						<Label>Account ID</Label>
						<Input value={settings.id} readOnly className="font-mono text-sm" />
					</div>
					<div className="space-y-2">
						<Label>Member Since</Label>
						<Input
							value={new Date(settings.created_at).toLocaleDateString()}
							readOnly
						/>
					</div>
				</CardContent>
			</Card>

			{/* API Credentials */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconKey className="h-5 w-5" />
						API Credentials
					</CardTitle>
					<CardDescription>
						Credentials for integrating with our Merchant API
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label>API Key</Label>
						<div className="flex gap-2">
							<Input
								value={settings.api_key || "Not available"}
								readOnly
								className="font-mono text-sm"
								type="password"
							/>
							<Button variant="outline" size="icon">
								<IconKey className="h-4 w-4" />
							</Button>
						</div>
						<p className="text-xs text-muted-foreground">
							Keep your API key secret. Never share it with anyone.
						</p>
					</div>
					<Separator />
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium">API Documentation</p>
							<p className="text-xs text-muted-foreground">
								View our API documentation for integration details
							</p>
						</div>
						<Button variant="outline" size="sm">
							View Docs
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Webhook Settings */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconWebhook className="h-5 w-5" />
						Webhook Settings
					</CardTitle>
					<CardDescription>
						Configure webhook URL for order status notifications
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label>Webhook URL</Label>
						<Input
							value={settings.callback_url || "Not configured"}
							readOnly
							placeholder="https://your-domain.com/webhook"
						/>
						<p className="text-xs text-muted-foreground">
							We will send POST requests to this URL when order status changes.
						</p>
					</div>
					<Separator />
					<div className="space-y-2">
						<Label>Webhook Secret (Optional)</Label>
						<Input
							type="password"
							value="••••••••••••"
							readOnly
							placeholder="Secret for webhook signature verification"
						/>
						<p className="text-xs text-muted-foreground">
							Used to verify webhook requests are from our servers.
						</p>
					</div>
				</CardContent>
			</Card>

			{/* Balance Information */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<IconCreditCard className="h-5 w-5" />
						Account Balance
					</CardTitle>
					<CardDescription>
						Your current available balance for withdrawals
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="text-3xl font-bold text-purple-600">
						৳{Number(settings.balance).toLocaleString()}
					</div>
					<p className="text-sm text-muted-foreground">
						Available for withdrawal
					</p>
					<Separator />
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium">Request Withdrawal</p>
							<p className="text-xs text-muted-foreground">
								Transfer funds to your bank account
							</p>
						</div>
						<Button size="sm">
							Request
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

function useMerchantSettings() {
	return useQuery({
		queryKey: QUERY_KEYS.MERCHANT_SETTINGS,
		queryFn: async () => {
			const response = await api.get<MerchantSettings>(
				API_ENDPOINTS.MERCHANT_DASHBOARD_SETTINGS
			)
			return response.data.data
		},
		staleTime: CACHE_TIME.DEFAULT,
	})
}

interface MerchantSettings {
	id: string
	name: string
	email: string
	commission_rate: number
	callback_url?: string
	api_key?: string
	balance: string
	status: string
	created_at: string
}
